import { parseFullName } from 'parse-full-name';

import type * as api from '@gitbook/api-client';
import { getShortDate } from '@gitbook/util-format';

/**
 * Formats the standard author and date fallback title.
 */
export function formatChangesTitle(
    author: Pick<api.User, 'displayName'>,
    createdAt: Date | string | number
): string {
    const { first } = parseFullName(author.displayName);
    const name = first || author.displayName;
    const date = getShortDate(new Date(createdAt));

    return `${name}'s ${date} changes`;
}

/**
 * Return a change request's subject or its standard author/date fallback.
 */
export function getChangeRequestSubject(changeRequest: {
    subject?: string | null;
    createdBy: Pick<api.User, 'displayName'>;
    createdAt: Date | string | number;
}): string {
    if (changeRequest.subject?.trim()) {
        return changeRequest.subject;
    }

    return formatChangesTitle(changeRequest.createdBy, changeRequest.createdAt);
}

/** Return the first line of a Git commit message as its revision title. */
export function getGitCommitTitle(message: string): string {
    return message.split('\n')[0]?.trim() ?? '';
}

/**
 * Resolve the title and number displayed for an external Git pull or merge request.
 */
export function getGitPullRequestBreadcrumb(
    message: string,
    url?: string
): { title: string; number: number } | null {
    const lines = message.split('\n').map((line) => line.trim());
    const title = lines[0] ?? '';
    const githubMerge = title.match(/^Merge pull request #(\d+)(?:\s+from\b.*)?$/i);

    if (githubMerge?.[1]) {
        return {
            title: getFirstNonEmptyLine(lines.slice(1)) ?? 'Pull request',
            number: Number(githubMerge[1]),
        };
    }

    const gitlabMergeRequest = message.match(/^See merge request .+!(\d+)\s*$/m);
    if (gitlabMergeRequest?.[1]) {
        const titleLines = /^Merge branch .+ into .+$/i.test(title) ? lines.slice(1) : lines;
        return {
            title:
                getFirstNonEmptyLine(
                    titleLines.filter((line) => !line.startsWith('See merge request '))
                ) ?? 'Merge request',
            number: Number(gitlabMergeRequest[1]),
        };
    }

    const titleNumber = title.match(/\s*\(#(\d+)\)\s*$/);
    if (titleNumber?.[1]) {
        return {
            title: title.slice(0, titleNumber.index).trim() || 'Pull request',
            number: Number(titleNumber[1]),
        };
    }

    const urlNumber = url?.match(/\/(pull|merge_requests)\/(\d+)(?:[/?#]|$)/);
    if (urlNumber?.[2]) {
        return {
            title: title || (urlNumber[1] === 'merge_requests' ? 'Merge request' : 'Pull request'),
            number: Number(urlNumber[2]),
        };
    }

    return null;
}

function getFirstNonEmptyLine(lines: string[]): string | null {
    return lines.find(Boolean) ?? null;
}
